// istanbul ignore file
import React, { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { Box, Grid, Typography } from '@mui/material'
import LinearProgress, { type LinearProgressProps } from '@mui/material/LinearProgress'
import { styled } from '@mui/material/styles'
import LanguageStrings from '../../../i18n/locales'
import { type ConnectedProps, connect, useDispatch } from 'react-redux'
import { type RootState } from '../../../store'
import { getHealthScore, getEnabledAlarmsbyLabel } from '../../asset-dashboard/redux/actionCreator'
import { type HealthScoreState } from '../../asset-dashboard/redux/healthScoreSlice'
import { type AlarmScoreState } from '../../asset-dashboard/redux/assetAlarmSlice'
import AssetAlert from './assetAlert'
import { reset } from './../redux/assetAlarmSlice'
import './assetDashboard.css'

const AssetDashboardStrings = LanguageStrings().AssetDashboardStrings

interface CustomLinearProgressProps extends LinearProgressProps {
  lineColor?: string
  filledColor?: string
}
// Custom Styled LinearProgress
const CustomLinearProgress = styled(LinearProgress, {
  shouldForwardProp: (prop) => prop !== 'lineColor' && prop !== 'filledColor'
})<CustomLinearProgressProps>(({ lineColor, filledColor, theme }) => ({
  height: 15,
  borderRadius: 1,
  backgroundColor: lineColor ?? theme.palette.grey[300],
  '& .MuiLinearProgress-bar': {
    backgroundColor: filledColor ?? theme.palette.primary.main
  }
}))

export type Order = 'asc' | 'desc'
export interface Sort {
  order: Order
  orderBy: string
}

const AssetScore = (props: PropsFromRedux): JSX.Element => {
  const [safeSort, setSafeSort] = React.useState<Sort>({ order: 'desc', orderBy: '' })
  const [reliSort, setReliSort] = React.useState<Sort>({ order: 'desc', orderBy: '' })
  const [effiSort, setEffiSort] = React.useState<Sort>({ order: 'desc', orderBy: '' })
  const [noneSort, setNoneSort] = React.useState<Sort>({ order: 'desc', orderBy: '' })
  const dispatch = useDispatch()
  const stateLocation = useLocation()

  interface StateLocation {
    parentSiteId: string
    parentAssetId: string
    siteName: string
    assetName: string
    deviceName: string
  }
  const navigateState = stateLocation.state as StateLocation | null
  const parentSiteId = (navigateState != null) ? navigateState.parentSiteId : ''
  const parentAssetId = (navigateState != null) ? navigateState.parentAssetId : ''

  useEffect(() => {
    if (parentAssetId !== '') {
      props.getHealthScore(parentSiteId, parentAssetId)
      props.getEnabledAlarmsbyLabel(parentSiteId, parentAssetId, 'safety', safeSort.order)
      props.getEnabledAlarmsbyLabel(parentSiteId, parentAssetId, 'reliability', reliSort.order)
      props.getEnabledAlarmsbyLabel(parentSiteId, parentAssetId, 'efficiency', effiSort.order)
      props.getEnabledAlarmsbyLabel(parentSiteId, parentAssetId, 'none', noneSort.order)
    }
  }, [parentSiteId, parentAssetId])

  useEffect(() => {
    return () => {
      dispatch(reset())
    }
  }, [])

  useEffect(() => {
    props.getEnabledAlarmsbyLabel(parentSiteId, parentAssetId, 'safety', safeSort.order)
  }, [safeSort])

  useEffect(() => {
    props.getEnabledAlarmsbyLabel(parentSiteId, parentAssetId, 'reliability', reliSort.order)
  }, [reliSort])

  useEffect(() => {
    props.getEnabledAlarmsbyLabel(parentSiteId, parentAssetId, 'efficiency', effiSort.order)
  }, [effiSort])

  useEffect(() => {
    props.getEnabledAlarmsbyLabel(parentSiteId, parentAssetId, 'none', noneSort.order)
  }, [noneSort])

  return (
    <Box sx={{ p: 4, backgroundColor: '#212121', color: '#FFF', minHeight: '100vh' }}>
      <Box style={{ width: '400px' }} display="flex" justifyContent="space-between">
        <div>
          <Typography gutterBottom style={{ fontSize: '16px', fontFamily: 'Honeywell Sans Web' }}>
            {AssetDashboardStrings.OverallAssetScore}
          </Typography>
        </div>
        <div>
          <Typography gutterBottom style={{ color: props.healthScoreData.scoreData > 80 ? 'green' : props.healthScoreData.scoreData > 60 ? 'orange' : 'red', fontWeight: 'bold', fontSize: '24px', fontFamily: 'Honeywell Sans Web' }}>
            {props.healthScoreData.scoreData}%
          </Typography>
        </div>
      </Box>
      <Box style={{ width: '400px', marginBottom: '22px' }}>
        <CustomLinearProgress
          variant="determinate" value={props.healthScoreData.scoreData} lineColor='#5E5E5E'
          filledColor={props.healthScoreData.scoreData > 80 ? 'green' : props.healthScoreData.scoreData > 60 ? 'orange' : 'red'} />
      </Box>
      <Grid container spacing={2}>
        <AssetAlert
          sort={safeSort} setSort={setSafeSort}
          isLoading={props?.assetAlarmData?.isSafeLoading}
          title='SAFETY' infoText={AssetDashboardStrings.SAFETY_INFO}
          color={props?.assetAlarmData?.safetyData?.labelScores?.scores?.Safetyscore > 80 ? 'green' : props?.assetAlarmData?.safetyData?.labelScores?.scores?.Safetyscore > 60 ? 'orange' : 'red'}
          alarms={props?.assetAlarmData?.safetyData?.alarms} score={props?.assetAlarmData?.safetyData?.labelScores?.scores?.Safetyscore ?? 0}
        />
        <AssetAlert
          sort={reliSort} setSort={setReliSort}
          isLoading={props?.assetAlarmData?.isReliLoading}
          title='RELIABILITY' infoText={AssetDashboardStrings.RELIABILITY_INFO}
          color={props?.assetAlarmData?.reliabilityData?.labelScores?.scores?.Reliabilityscore > 80 ? 'green' : props?.assetAlarmData?.reliabilityData?.labelScores?.scores?.Reliabilityscore > 60 ? 'orange' : 'red'}
          alarms={props?.assetAlarmData?.reliabilityData?.alarms} score={props?.assetAlarmData?.reliabilityData?.labelScores?.scores?.Reliabilityscore ?? 0}
        />
        <AssetAlert
          sort={effiSort} setSort={setEffiSort}
          isLoading={props?.assetAlarmData?.isEffiLoading}
          title='EFFICIENCY' infoText={AssetDashboardStrings.EFFICIENCY_INFO}
          color={props?.assetAlarmData?.efficiencyData?.labelScores?.scores?.Efficiencyscore > 80 ? 'green' : props?.assetAlarmData?.efficiencyData?.labelScores?.scores?.Efficiencyscore > 60 ? 'orange' : 'red'}
          alarms={props?.assetAlarmData?.efficiencyData?.alarms} score={props?.assetAlarmData?.efficiencyData?.labelScores?.scores?.Efficiencyscore ?? 0}
        />
        <AssetAlert
          sort={noneSort} setSort={setNoneSort}
          isLoading={props?.assetAlarmData?.isNoneLoading}
          infoText={AssetDashboardStrings.NONE_INFO} title='NONE' color='' alarms={props?.assetAlarmData?.noneData?.alarms} score={0}
        />
      </Grid>
    </Box>
  )
}

interface DispatchToProps {
  getHealthScore: (siteId: string, assetId: string) => void
  getEnabledAlarmsbyLabel: (siteId: string, assetId: string, label: string, SortParams: string) => void
}
const mapDispatchToProps = (dispatch: any): DispatchToProps => ({
  getHealthScore: (siteId: string, assetId: string) => dispatch(getHealthScore(siteId, assetId)),
  getEnabledAlarmsbyLabel: (siteId: string, assetId: string, label: string, SortParams: string) => dispatch(getEnabledAlarmsbyLabel(siteId, assetId, label, SortParams))
})

interface StateToProps {
  healthScoreData: HealthScoreState
  assetAlarmData: AlarmScoreState
}
const mapStateToProps = (state: RootState): StateToProps => ({
  healthScoreData: state.assetHealthScore,
  assetAlarmData: state.assetAlarmState
})
const connector = connect(mapStateToProps, mapDispatchToProps)
type PropsFromRedux = ConnectedProps<typeof connector>

export default connector(AssetScore)
